import { differenceInDays, format } from 'date-fns';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Streamdown } from 'streamdown';

import {
	ArrowLeftOutline,
	Box,
	Button,
	Card,
	Divider,
	Flex,
	Heading,
	HStack,
	Link,
	ListItem,
	OrderedList,
	rem,
	Skeleton,
	Text,
	UnorderedList,
	VStack,
} from '@bonusly/design-system';

import { Bizy, type BizyState } from '../../../components/bizy';
import { bizyTableComponents } from '../../../components/bizy-side-panel/components/bizy-markdown-table';
import { useBizyUserConversation } from '../../../queries/bizy-conversations';
import type { BizyUserMessage } from '../../../schemas/bizy-conversations';
import { getDateFnsLocale } from '../../../utils/date';

interface ConversationDetailProps {
	conversationId: string;
	onBack: () => void;
	onResumeConversation: (conversationId: string, messages: BizyUserMessage[]) => void;
}

const REHYDRATION_WINDOW_DAYS = 30;

const UserMessageBubble: FC<{ message: BizyUserMessage; locale: string }> = ({ message, locale }) => (
	<Box alignSelf="flex-end" maxWidth="85%">
		<Text fontSize="xs" color="text.softer" textAlign="right" mb={1}>
			{format(message.createdAt, 'PPp', { locale: getDateFnsLocale(locale) })}
		</Text>
		<Box
			bg="secondary.bgAlpha"
			color="text.default"
			px={4}
			py={3}
			borderRadius="lg"
			borderBottomRightRadius="sm"
			border="1px solid"
			borderColor="secondary.bgAlpha"
		>
			<Text fontSize="sm" whiteSpace="pre-wrap">
				{message.userMessage}
			</Text>
		</Box>
	</Box>
);

const BizyMessageBubble: FC<{ message: BizyUserMessage; locale: string }> = ({ message, locale }) => (
	<Flex direction="column" alignSelf="flex-start" maxWidth="85%">
		<HStack spacing={1} mb={1}>
			<Bizy state={(message.emotion as BizyState) || 'curious'} size="sm" />
			<Text fontSize="xs" color="text.softer">
				{format(message.createdAt, 'PPp', { locale: getDateFnsLocale(locale) })}
			</Text>
		</HStack>
		<Box
			color="text.default"
			sx={{
				'p + p': { marginTop: '0.5em' },
				'ul, ol': { marginTop: '0.5em', marginBottom: '0.5em' },
				'p:first-of-type': { marginTop: 0 },
				'p:last-of-type': { marginBottom: 0 },
			}}
		>
			<Streamdown
				mode="static"
				components={{
					a: ({ href, children }) => (
						<Link href={href} color="text.interactive" target="_blank" rel="noopener noreferrer">
							{children}
						</Link>
					),
					p: ({ children }) => (
						<Text fontSize="sm" color="text.default">
							{children}
						</Text>
					),
					ol: ({ children }) => (
						<OrderedList fontSize="sm" color="text.default">
							{children}
						</OrderedList>
					),
					ul: ({ children }) => (
						<UnorderedList fontSize="sm" color="text.default">
							{children}
						</UnorderedList>
					),
					li: ({ children }) => <ListItem>{children}</ListItem>,
					...(bizyTableComponents('sm') as Record<string, unknown>),
					pre: ({ children }) => <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>{children}</pre>,
					code: ({ children }) => (
						<Text as="code" fontSize="sm" bg="bg.muted" px={1} borderRadius="sm">
							{children}
						</Text>
					),
				}}
			>
				{message.bizyResponse}
			</Streamdown>
		</Box>

		{/* Feedback indicator */}
		{message.feedbackType && (
			<Text fontSize="xs" color={message.feedbackType === 'positive' ? 'success.fg' : 'error.fg'} mt={1}>
				{message.feedbackType === 'positive' ? '👍' : '👎'}
			</Text>
		)}
	</Flex>
);

export const ConversationDetail: FC<ConversationDetailProps> = ({ conversationId, onBack, onResumeConversation }) => {
	const { t, i18n } = useTranslation('bizy_history');
	const { data: conversation, isLoading } = useBizyUserConversation(conversationId);

	const canResume = useMemo(() => {
		if (!conversation?.messages.length) return false;
		const lastMessage = conversation.messages[conversation.messages.length - 1];
		return differenceInDays(new Date(), lastMessage.createdAt) <= REHYDRATION_WINDOW_DAYS;
	}, [conversation]);

	if (isLoading) {
		return (
			<VStack spacing={4} align="stretch">
				<Skeleton height={rem(40)} />
				<Skeleton height={rem(400)} />
			</VStack>
		);
	}

	if (!conversation) {
		return (
			<Card p={8} textAlign="center">
				<Text color="text.soft">{t('detail.not_found')}</Text>
				<Button onClick={onBack} mt={4} size="sm" variant="ghost">
					{t('detail.back')}
				</Button>
			</Card>
		);
	}

	return (
		<VStack spacing={4} align="stretch">
			{/* Header */}
			<Flex justify="space-between" align="center">
				<Button onClick={onBack} size="sm" variant="ghost" leftIcon={<ArrowLeftOutline size="sm" />}>
					{t('detail.back')}
				</Button>
				{canResume && (
					<Button size="sm" onClick={() => onResumeConversation(conversationId, conversation.messages)}>
						{t('detail.resume')}
					</Button>
				)}
			</Flex>

			{/* Metadata */}
			<HStack spacing={4}>
				{conversation.pagePath && (
					<Text fontSize="xs" color="text.softer" fontFamily="mono">
						{conversation.pagePath}
					</Text>
				)}
				<Text fontSize="xs" color="text.softer">
					{t('detail.message_count', { count: conversation.messages.length })}
				</Text>
			</HStack>

			{/* Messages */}
			<Card p={6}>
				<Heading size="sm" mb={4} color="text.default">
					{t('detail.conversation_heading')}
				</Heading>
				<VStack spacing={4} align="stretch" divider={<Divider borderColor="border.soft" />}>
					{conversation.messages.map((message) => (
						<VStack key={message.id} spacing={3} align="stretch">
							<UserMessageBubble message={message} locale={i18n.language} />
							<BizyMessageBubble message={message} locale={i18n.language} />
						</VStack>
					))}
				</VStack>
			</Card>
		</VStack>
	);
};
